import { useEffect, useState } from "react";

import Field from "../../../shared/ui/Field/Field";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";

import { postCommunication } from "../api/communications";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientLogCommunicationModal — feature-local T-3 modal asset.
//
// Closed-class reference: architecture-freeze-r8.md §4 T-3
// ("section owns slice + modal; parent header has a presentation-alias
// trigger that calls a one-method ref bridge"). Bridge constraints
// per I2 §"R7 §6.4" proposal hold on the section, not on this file —
// this widget never sees the parent. It receives `open` / `onClose` /
// `clientId` / `onSaved` from its single owner (the activity section)
// and knows nothing else.
//
// Ownership:
//   * open/close state — owned by [[ClientActivitySection]] via plain
//     useState; this component is fully controlled.
//   * mutation — owned here (POST /communications/ via the feature-
//     local API wrapper). The section is NOT involved in the mutation.
//   * post-success freshness — owned by the section: this widget calls
//     onSaved() once the POST resolves; the section closes the modal
//     and refetches its own slice. No parent involvement, no
//     cross-section refresh, no orchestration.
//
// Reuses the intra-feature `.feature-client-detail__modal*` CSS rules
// introduced for the T-1 ClientEditModal — same feature scope, NOT a
// shared modal primitive. No portal, no provider, no registry, no
// shared overlay host. Sibling of [[ClientEditModal]] and
// [[ClientContactsEditModal]].
//
// Form fields kept deliberately minimal: type / direction / content
// (the three fields the backend serializer accepts on POST). The
// design HTML's "property" search and "when" datetime fields are
// intentionally NOT rendered — `created_at` is server-side
// auto_now_add and `property` would require a picker the system does
// not have. Adding them would be R7 §12.5 forbidden invention.
//
// Effects in this primitive are limited to the allowed UI-local
// effects per CLAUDE.md "Presentation Primitive Rules": ESC-key
// handler, body scroll lock, and a draft-reset on (re)open. No
// selectors, no dispatch, no imperative handle, no registry, no
// portal, no shared modal host.

const TYPE_OPTIONS = [
  { value: "call", label: "Дзвінок" },
  { value: "email", label: "Email" },
  { value: "message", label: "Повідомлення" },
];

const DIRECTION_OPTIONS = [
  { value: "out", label: "Вихідне" },
  { value: "in", label: "Вхідне" },
];

const EDITABLE_KEYS = ["type", "direction", "content"];

const INITIAL_DRAFT = { type: "call", direction: "out", content: "" };

function ClientLogCommunicationModal({ open, onClose, clientId, onSaved }) {
  const [draft, setDraft] = useState(INITIAL_DRAFT);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [submitState, setSubmitState] = useState("idle");

  // Reset draft + clear errors every time the modal opens. The modal
  // is mounted but hidden when closed (open=false short-circuits the
  // render below) — when it transitions back to open we want a fresh
  // form, not the last attempt's state. Local effect only.
  useEffect(() => {
    if (open) {
      setDraft(INITIAL_DRAFT);
      setFieldErrors({});
      setFormError(null);
      setSubmitState("idle");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submitting = submitState === "submitting";

  const handleFieldChange = (field) => (event) => {
    const next = event.target.value;
    setDraft((prev) => ({ ...prev, [field]: next }));
    setFieldErrors((prev) => {
      if (!prev[field] && !prev._form) return prev;
      const out = { ...prev };
      delete out[field];
      delete out._form;
      return out;
    });
    setFormError(null);
  };

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (submitting) return;

    setSubmitState("submitting");
    setFieldErrors({});
    setFormError(null);

    try {
      await postCommunication(clientId, {
        type: draft.type,
        direction: draft.direction,
        content: draft.content ?? "",
      });
      setSubmitState("saved");
      onSaved();
    } catch (err) {
      const parsed = parseDrfFieldErrors(err, EDITABLE_KEYS);
      setFieldErrors(parsed);
      setFormError(parsed._form || messageFromError(err));
      setSubmitState("error");
    }
  };

  return (
    <div
      className="feature-client-detail__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-log-comm-modal-title"
    >
      <div
        className="feature-client-detail__modal-backdrop"
        aria-hidden="true"
        onClick={onClose}
      />
      <div className="feature-client-detail__modal-dialog">
        <header className="feature-client-detail__modal-head">
          <h2
            id="client-log-comm-modal-title"
            className="feature-client-detail__modal-title"
          >
            <i className="fa-solid fa-phone" aria-hidden="true" />
            <span>Залогувати взаємодію</span>
          </h2>
          <button
            type="button"
            className="feature-client-detail__modal-close"
            onClick={onClose}
            aria-label="Закрити вікно логування взаємодії"
            title="Закрити"
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <div className="feature-client-detail__modal-body">
          <form className="ui-form" onSubmit={handleSubmit} noValidate>
            <div className="ui-form__rows">
              <Field
                id="client-log-comm-type"
                label="Тип"
                required
                error={fieldErrors.type}
              >
                <Select
                  name="type"
                  value={draft.type}
                  onChange={handleFieldChange("type")}
                  options={TYPE_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-log-comm-direction"
                label="Напрямок"
                required
                error={fieldErrors.direction}
              >
                <Select
                  name="direction"
                  value={draft.direction}
                  onChange={handleFieldChange("direction")}
                  options={DIRECTION_OPTIONS}
                  disabled={submitting}
                />
              </Field>

              <Field
                id="client-log-comm-content"
                label="Зміст"
                help="Конспект розмови, тіло листа або суть повідомлення."
                error={fieldErrors.content}
              >
                <Textarea
                  name="content"
                  value={draft.content}
                  onChange={handleFieldChange("content")}
                  rows={5}
                  placeholder="Опишіть взаємодію…"
                  disabled={submitting}
                />
              </Field>
            </div>

            <div className="ui-form__messages">
              {formError ? (
                <InlineError className="ui-form__message">
                  {formError}
                </InlineError>
              ) : null}
            </div>

            <div className="ui-form__actions">
              <button
                type="button"
                className="ui-form__button ui-form__button--ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Скасувати
              </button>
              <button
                type="submit"
                className="ui-form__button ui-form__button--primary"
                disabled={submitting}
                data-loading={submitting ? "true" : undefined}
              >
                {submitting ? (
                  <Spinner size="sm" label="Збереження" />
                ) : (
                  "Зберегти"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ClientLogCommunicationModal;
